import { useState, useEffect, useCallback } from 'react';
import { Check, HelpCircle, X as XIcon, Users } from 'lucide-react';
import api from '../lib/api';

const RSVP_OPTIONS = [
  { key: 'going', label: 'Going',   emoji: '✅', color: '#3F5200', bg: 'bg-[#B6E600]/25', border: 'border-[#B6E600]' },
  { key: 'maybe', label: 'Maybe',   emoji: '🤔', color: '#7C5400', bg: 'bg-[#FFF1C7]', border: 'border-[#F2E59A]' },
  { key: 'cant',  label: "Can't make it", emoji: '❌', color: '#B22222', bg: 'bg-[#FF3B30]/10', border: 'border-[#FF3B30]/40' },
];

/**
 * PlanChatRsvpBar — pinned beneath the plan-chat header. Members tap to
 * declare attendance; tapping again clears. Host sees the breakdown
 * via "Who's coming?" button.
 */
export default function PlanChatRsvpBar({ planId, isHost }) {
  const [data, setData] = useState({ me: '', counts: { going: 0, maybe: 0, cant: 0 }, going: [], maybe: [], cant: [] });
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/plans/${planId}/chat-rsvp`);
      setData(data || {});
    } catch {/* ignore */}
  }, [planId]);

  useEffect(() => { refresh(); }, [refresh]);

  const setStatus = async (next) => {
    if (busy) return;
    setBusy(true);
    const send = data.me === next ? '' : next;
    try {
      await api.put(`/plans/${planId}/chat-rsvp`, { status: send });
      await refresh();
    } catch {/* ignore */}
    setBusy(false);
  };

  const total = (data.counts?.going || 0) + (data.counts?.maybe || 0) + (data.counts?.cant || 0);

  return (
    <>
      <div className="flex-shrink-0 px-3 py-2 bg-[#FBFAF4] border-b border-black/[0.04]" data-testid="plan-rsvp-bar">
        <div className="flex items-center gap-1.5 overflow-x-auto hide-scrollbar">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#9B9EA3] flex-shrink-0 pr-1">RSVP</span>
          {RSVP_OPTIONS.map(opt => {
            const active = data.me === opt.key;
            const count = data.counts?.[opt.key] || 0;
            return (
              <button
                key={opt.key}
                onClick={() => setStatus(opt.key)}
                data-testid={`rsvp-${opt.key}`}
                disabled={busy}
                className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border transition-all active:scale-95 ${active ? `${opt.bg} ${opt.border}` : 'bg-white border-black/[0.06] text-[#6B6E73]'}`}
                style={active ? { color: opt.color, borderColor: opt.color } : {}}
              >
                <span>{opt.emoji}</span>
                <span>{opt.label}</span>
                {count > 0 && <span className="ml-1 text-[10px] opacity-80">{count}</span>}
              </button>
            );
          })}
          {total > 0 && (
            <button onClick={() => setShowBreakdown(true)} data-testid="rsvp-who" className="flex-shrink-0 ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border bg-white border-black/[0.06] text-[#3F5200] active:scale-95">
              <Users size={12} /> Who's coming
            </button>
          )}
        </div>
      </div>

      {showBreakdown && (
        <div className="fixed inset-0 z-[10080] bg-black/40 flex items-end justify-center animate-fade-in" onClick={() => setShowBreakdown(false)} data-testid="rsvp-breakdown-overlay">
          <div className="w-full max-w-[480px] max-h-[80vh] bg-white rounded-t-3xl flex flex-col animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-black/[0.04]">
              <p className="text-base font-bold text-[#111111]">Attendance</p>
              <button onClick={() => setShowBreakdown(false)} className="p-1 text-[#9B9EA3]"><XIcon size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto hide-scrollbar p-4 space-y-4">
              {RSVP_OPTIONS.map(opt => {
                const list = data[opt.key] || [];
                if (list.length === 0) return null;
                return (
                  <div key={opt.key} data-testid={`rsvp-breakdown-${opt.key}`}>
                    <p className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider mb-2">
                      {opt.emoji} {opt.label} · {list.length}
                    </p>
                    <div className="space-y-1.5">
                      {list.map(u => (
                        <div key={u.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-2xl">
                          {u.profile_photo
                            ? <img loading="lazy" decoding="async" src={u.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                            : <div className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[10px] font-bold text-[#9B9EA3]">{(u.display_name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}</div>}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#111111] truncate">{u.display_name}</p>
                            <p className="text-[11px] text-[#9B9EA3] truncate">@{u.username}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {total === 0 && (
                <p className="text-center text-xs text-[#9B9EA3] py-6">No RSVPs yet.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
